/** Clamp `value` into inclusive `[min, max]`. */
export function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  if (max < min) return min;
  return Math.min(max, Math.max(min, value));
}

/**
 * Snap `value` to the nearest step grid relative to `min`, then clamp.
 * When `step` is 0 or non-finite, returns the clamped value unchanged.
 */
export function quantize(value: number, min: number, max: number, step: number): number {
  const clamped = clamp(value, min, max);
  if (!Number.isFinite(step) || step <= 0) return clamped;
  const steps = Math.round((clamped - min) / step);
  return clamp(min + steps * step, min, max);
}

export type ValueScale = "linear" | "log";

/** Map a 0–1 normalized position onto a quantized [min, max] range. */
export function valueFromNorm(
  norm: number,
  min: number,
  max: number,
  step: number,
  scale: ValueScale = "linear",
): number {
  const n = clamp(norm, 0, 1);
  if (scale === "log") {
    const lo = Math.max(min, Number.EPSILON);
    const hi = Math.max(max, lo * (1 + Number.EPSILON));
    const value = Math.exp(Math.log(lo) + n * (Math.log(hi) - Math.log(lo)));
    return quantize(value, min, max, step);
  }
  return quantize(min + n * (max - min), min, max, step);
}

/** Map a value in [min, max] to a 0–1 normalized position. */
export function normFromValue(
  value: number,
  min: number,
  max: number,
  scale: ValueScale = "linear",
): number {
  if (max === min) return 0;
  if (scale === "log") {
    const lo = Math.max(min, Number.EPSILON);
    const hi = Math.max(max, lo * (1 + Number.EPSILON));
    const v = Math.max(value, lo);
    return clamp((Math.log(v) - Math.log(lo)) / (Math.log(hi) - Math.log(lo)), 0, 1);
  }
  return clamp((value - min) / (max - min), 0, 1);
}

/** Fine step: one tenth of `step`, or a small fraction of the range when step is large. */
export function fineStep(step: number, min: number, max: number): number {
  if (Number.isFinite(step) && step > 0) {
    const fine = step / 10;
    return fine > 0 ? fine : step;
  }
  const span = Math.abs(max - min);
  return span > 0 ? span / 1000 : 0.001;
}
